<template>
  <or-sidebar
    data-component="base-settings-page-sidebar"
    :width="MIN_WIDTH"
    :min-width="MIN_WIDTH"
    :max-width="MAX_WIDTH"
  >
    <template #header>
      <div class="title-wrapper">
        <div class="title">
          <or-input
            v-if="isEditMode"
            v-model="localTitle"
            :error="titleError"
            required
            @keydown.enter="onEditDone"
          />
          <span
            v-else
            data-title="sidebar-name"
          >
            {{ localTitle }}
          </span>
        </div>
        <template v-if="isTitleEditable">
          <or-icon-button
            v-if="isEditMode"
            icon="done"
            :class="{ 'icon-disabled': titleError }"
            :size="IconSize.L"
            @click="onEditDone"
          />
          <or-icon-button
            v-else
            icon="mode_edit"
            :size="IconSize.L"
            @click="isEditMode = true"
          />
        </template>
      </div>
    </template>

    <template #default>
      <slot name="default" />

      <!-- Confirm closing popup has different labels in "create" / "update" modes -->
      <or-confirm
        :is-open="isClosePopupOpen"
        :title-text="$t('HITL.modules.common-settings.sidebar.close-title')"
        @confirm="$emit('close-confirm')"
        @close="$emit('close-cancel')"
      >
        <template #default>
          <i18n
            v-if="tItemLabel"
            slot="content"
            path="HITL.modules.common-settings.sidebar.close-description-update"
          >
            <strong
              slot="text"
              style="word-break: break-all"
            >
              {{ tItemLabel }}
            </strong>
          </i18n>

          <template v-else>
            {{ $t('HITL.modules.common-settings.sidebar.close-description-create') }}
          </template>
        </template>
      </or-confirm>

      <!-- Confirm delete popup should be applied to an existing item -->
      <or-confirm
        :is-open="isDeletePopupOpen"
        :title-text="$t(deletePopupTitle, { item: itemLabel })"
        :loading="deleteButtonState === 'loading'"
        :auto-close-on-confirm="false"
        confirm-type="d"
        @confirm="$emit('delete-confirm')"
        @close="$emit('delete-cancel')"
      >
        <template #default>
          <i18n
            slot="content"
            :path="deletePopupMessage"
          >
            <strong
              slot="item"
              style="word-break: break-word"
            >
              {{ tItemLabel }}
            </strong>
          </i18n>
        </template>
      </or-confirm>
    </template>

    <template #footer>
      <div class="actions">
        <or-tooltip v-if="deleteButtonTooltip">
          <div>
            <or-button
              v-if="showDeleteButton"
              data-button="delete"
              variant="outlined"
              disabled
            >
              {{ $t('HITL.common.actions.delete') }}
            </or-button>
          </div>
          <template #content>
            {{ deleteButtonTooltip }}
          </template>
        </or-tooltip>

        <or-button
          v-else-if="showDeleteButton"
          data-button="delete"
          color="danger"
          variant="outlined"
          :loading="deleteButtonState === 'loading'"
          :disabled="deleteButtonState === 'disabled'"
          @click="$emit('delete')"
        >
          {{ $t('HITL.common.actions.delete') }}
        </or-button>

        <div class="buttons">
          <or-button
            data-button="cancel"
            variant="outlined"
            :loading="cancelButtonState === 'loading'"
            :disabled="cancelButtonState === 'disabled'"
            @click="$emit('cancel')"
          >
            {{ $t(cancelButtonLabel) }}
          </or-button>

          <or-button
            v-if="showSuccessButton"
            data-button="save"
            :loading="successButtonState === 'loading'"
            :disabled="successButtonState === 'disabled'"
            @click="onSuccessClick"
          >
            {{ successButtonLabel }}
          </or-button>
        </div>
      </div>
    </template>
  </or-sidebar>
</template>

<script lang="ts">
import _ from 'lodash';
import {
  computed,
  defineComponent,
  getCurrentInstance,
  PropType,
  ref,
  watch,
} from 'vue-demi';
import { TranslateResult } from 'vue-i18n';
import {
  OrSidebarV3 as OrSidebar,
  OrButtonV3 as OrButton,
  OrConfirmV3 as OrConfirm,
  OrTooltipV3 as OrTooltip,
  OrInputV3 as OrInput,
  OrIconButtonV3 as OrIconButton,
  IconSize,
} from '@onereach/ui-components-25';

type ButtonState = '' | 'loading' | 'disabled' | 'hidden';

const MIN_WIDTH = 360;
const MAX_WIDTH = 640;
const MAX_TITLE_LENGTH = 100;

/** TODO: Add more features like buttons slot */
export default defineComponent({
  name: 'BaseSettingsPageSidebar',
  components: {
    OrSidebar,
    OrButton,
    OrConfirm,
    OrTooltip,
    OrInput,
    OrIconButton,
  },
  props: {
    title: {
      type: String as PropType<string | TranslateResult>,
      default: '',
    },
    actionsType: {
      type: String as PropType<'create' | 'update'>,
      default: 'create',
    },
    successButtonState: {
      type: String as PropType<ButtonState>,
      default: '',
    },
    cancelButtonState: {
      type: String as PropType<ButtonState>,
      default: '',
    },
    deleteButtonState: {
      type: String as PropType<ButtonState>,
      default: '',
    },
    deleteButtonTooltip: {
      type: String as PropType<TranslateResult>,
      default: '',
    },
    itemLabel: {
      type: String,
      default: '',
    },
    createButtonLabel: {
      type: String,
      default: 'HITL.common.actions.create',
    },
    saveButtonLabel: {
      type: String,
      default: 'HITL.common.actions.save',
    },
    cancelButtonLabel: {
      type: String,
      default: 'HITL.common.actions.cancel',
    },
    deletePopupTitle: {
      type: String,
      default: 'HITL.modules.common-settings.sidebar.delete-title',
    },
    deletePopupMessage: {
      type: String,
      default: 'HITL.modules.common-settings.sidebar.delete-description',
    },
    isClosePopupOpen: Boolean,
    isDeletePopupOpen: Boolean,
    isTitleEditable: Boolean,
    maxTitleLength: {
      type: Number,
      default: MAX_TITLE_LENGTH,
    },
  },
  emits: [
    'create',
    'update',
    'cancel',
    'delete',
    'close-confirm',
    'close-cancel',
    'delete-confirm',
    'delete-cancel',
  ],
  setup(props, ctx) {
    const vm = getCurrentInstance()!;
    const isEditMode = ref(false);
    const localTitle = ref(props.title);

    watch(
      () => props.title,
      value => {
        localTitle.value = value;
      },
    );

    const titleError = computed(() => {
      if (localTitle.value === '') {
        return vm.proxy.$t('HITL.common.labels.required');
      } else if ((localTitle.value as string).length > props.maxTitleLength) {
        return vm.proxy.$t('HITL.ui-customization.workspace.components.max-length-error',
          { max: props.maxTitleLength });
      }
      return '';
    });

    function onSuccessClick() {
      switch (props.actionsType) {
        case 'create':
          return ctx.emit('create');
        case 'update':
        default:
          return ctx.emit('update');
      }
    }

    function onEditDone() {
      if (titleError.value) return;
      isEditMode.value = false;
      ctx.emit('update:title', localTitle.value);
    }

    const showSuccessButton = computed(() => {
      return props.successButtonState !== 'hidden';
    });

    const showDeleteButton = computed(() => {
      return props.actionsType === 'update'
        && props.deleteButtonState !== 'hidden';
    });

    const successButtonLabel = computed(() => {
      switch (props.actionsType) {
        case 'create':
          return vm.proxy.$t(props.createButtonLabel);
        case 'update':
        default:
          return vm.proxy.$t(props.saveButtonLabel);
      }
    });

    const tItemLabel = computed(() => {
      return _.truncate(props.itemLabel);
    });

    return {
      IconSize,
      MIN_WIDTH,
      MAX_WIDTH,
      isEditMode,
      localTitle,
      titleError,
      showSuccessButton,
      showDeleteButton,
      successButtonLabel,
      tItemLabel,
      onSuccessClick,
      onEditDone,
    };
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-settings-page-sidebar'] {
  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--s-4);

    .title {
      overflow: hidden;
      flex-grow: 1;
    }

    .icon-disabled {
      pointer-events: none;
      opacity: 0.25;
    }
  }

  .actions {
    display: flex;
    flex: 0 0 auto;

    .buttons {
      display: flex;
      gap: var(--s-4);
      margin-left: auto;
    }
  }

  [data-title='sidebar-name'] {
    display: inline-block;
    max-width: 100%;
    color: var(--or-c-on-background);
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}
</style>
